<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import { deviceCreateSchema, deviceUpdateSchema } from './device-schema'
import BBtn from '@lib/components/base/BBtn.vue'
import BInput from '@lib/components/base/BInput.vue'
import QrPreview from '@lib/components/shared/QrPreview.vue'
import { sdk } from '@/graphql/client'
import { useToast } from '@lib/composables/useToast'
import { useConfirm } from '@lib/composables/useConfirm'
import { buildLoginCode } from '@/lib/configCode'
import { buildDeepLink } from '@/lib/deepLink'
import { useClipboardStore } from '@/stores/clipboard'
import type { Device } from '@/composables/useDevicesData'

const props = defineProps<{ device: Device | null }>()
const emit = defineEmits<{ saved: []; deleted: []; cancel: [] }>()

const { t } = useI18n()
const toast = useToast()
const { confirm } = useConfirm()
const clipboard = useClipboardStore()

const isCreate = computed(() => props.device === null)

interface FormValues {
    name: string
    username: string
    password: string
}

function initialValues(): FormValues {
    return {
        name: props.device?.name ?? '',
        username: props.device?.username ?? '',
        password: ''
    }
}

const { handleSubmit, defineField, errors, resetForm, meta } = useForm<FormValues>({
    validationSchema: toTypedSchema(isCreate.value ? deviceCreateSchema : deviceUpdateSchema),
    initialValues: initialValues()
})

const [name] = defineField('name')
const [username] = defineField('username')
const [password] = defineField('password')

const submitting = ref(false)
const deleting = ref(false)

const qrCode = ref<string | null>(null)
const qrPassword = ref<string | null>(null)
const loadingQr = ref(false)

async function loadQr() {
    if (!props.device) return
    loadingQr.value = true
    try {
        const r = await sdk.AdminDeviceCredentials({ id: props.device.id })
        const pwd = r.admin?.device?.detail?.password ?? ''
        qrPassword.value = pwd
        qrCode.value = buildDeepLink(buildLoginCode(props.device.username, pwd))
    } catch {
        toast.error(t('common.errors.generic'))
    } finally {
        loadingQr.value = false
    }
}

function addToClipboard() {
    if (!props.device || qrPassword.value === null) return
    clipboard.add({
        kind: 'login',
        login: { username: props.device.username, password: qrPassword.value },
        item: { id: props.device.id, name: props.device.name || props.device.username }
    })
    toast.success(t('devices.qr.added'))
}

const onSubmit = handleSubmit(async vals => {
    submitting.value = true
    try {
        if (isCreate.value) {
            const r = await sdk.AdminCreateDevice({
                name: vals.name,
                username: vals.username,
                password: vals.password
            })
            if (r.admin?.device?.create?.ok) {
                toast.success(t('devices.toast.saved'))
                resetForm()
                emit('saved')
            } else {
                toast.error(t('common.errors.generic'))
            }
        } else {
            const r = await sdk.AdminUpdateDevice({
                id: props.device!.id,
                name: vals.name,
                username: vals.username,
                // Empty password = keep existing (backend skips when password === '')
                password: vals.password || undefined
            })
            if (r.admin?.device?.update?.ok) {
                toast.success(t('devices.toast.saved'))
                resetForm({ values: { ...vals, password: '' } })
                emit('saved')
            } else {
                toast.error(t('common.errors.generic'))
            }
        }
    } catch (e) {
        toast.error((e as Error).message)
    } finally {
        submitting.value = false
    }
})

async function onDelete() {
    if (!props.device) return
    const ok = await confirm({
        title: t('devices.confirm.deleteTitle'),
        message: t('devices.confirm.deleteBody', { name: props.device.name }),
        severity: 'danger'
    })
    if (!ok) return
    deleting.value = true
    try {
        const r = await sdk.AdminDeleteDevice({ id: props.device.id })
        if (r.admin?.device?.delete?.ok) {
            toast.success(t('devices.toast.deleted'))
            emit('deleted')
        } else {
            toast.error(t('common.errors.generic'))
        }
    } catch (e) {
        toast.error((e as Error).message)
    } finally {
        deleting.value = false
    }
}

async function onCancel() {
    if (meta.value.dirty) {
        const ok = await confirm({
            title: t('devices.confirm.unsavedChanges'),
            severity: 'danger'
        })
        if (!ok) return
    }
    emit('cancel')
}

watch(
    () => props.device?.id,
    () => resetForm({ values: initialValues() })
)

defineExpose({ isDirty: () => meta.value.dirty })
</script>

<template>
    <form class="space-y-4" @submit.prevent="onSubmit">
        <label class="block">
            <span class="text-xs font-semibold text-gray-600 mb-1 block">
                {{ t('devices.form.name') }} *
            </span>
            <BInput v-model="name" :invalid="!!errors.name" />
            <span v-if="errors.name" class="text-xs text-red mt-1 block">{{ errors.name }}</span>
        </label>

        <label class="block">
            <span class="text-xs font-semibold text-gray-600 mb-1 block">
                {{ t('devices.form.username') }} *
            </span>
            <BInput v-model="username" :invalid="!!errors.username" />
            <span v-if="errors.username" class="text-xs text-red mt-1 block">{{ errors.username }}</span>
        </label>

        <label class="block">
            <span class="text-xs font-semibold text-gray-600 mb-1 block">
                {{ t('devices.form.password') }}<span v-if="isCreate"> *</span>
            </span>
            <BInput v-model="password" type="password" :invalid="!!errors.password" />
            <span v-if="errors.password" class="text-xs text-red mt-1 block">{{ errors.password }}</span>
            <span v-else-if="!isCreate" class="text-xs text-gray-500 mt-1 block">
                {{ t('devices.form.passwordHint') }}
            </span>
        </label>

        <section v-if="props.device" class="border-t border-gray-200 pt-4 mt-4">
            <h3 class="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">
                {{ t('devices.qr.title') }}
            </h3>
            <div v-if="qrCode" class="flex items-start gap-4 flex-wrap">
                <QrPreview
                    :code="qrCode"
                    :label="props.device!.name || props.device!.username"
                    :subtitle="`${props.device!.username} · ID ${props.device!.id}`"
                    :size="160"
                />
                <BBtn severity="secondary" size="small" type="button" @click="addToClipboard">
                    {{ t('devices.qr.addToClipboard') }}
                </BBtn>
            </div>
            <BBtn v-else severity="secondary" size="small" type="button" :loading="loadingQr" @click="loadQr">
                {{ t('devices.qr.load') }}
            </BBtn>
        </section>

        <div class="flex items-center justify-between gap-2 pt-2 border-t border-gray-200">
            <div>
                <BBtn
                    v-if="!isCreate"
                    severity="danger"
                    size="small"
                    :disabled="deleting"
                    type="button"
                    @click="onDelete"
                >
                    {{ t('common.actions.delete') }}
                </BBtn>
            </div>
            <div class="flex gap-2">
                <BBtn severity="secondary" size="small" type="button" @click="onCancel">
                    {{ t('common.actions.cancel') }}
                </BBtn>
                <BBtn size="small" type="submit" :disabled="!meta.valid || !meta.dirty || submitting">
                    {{ t('common.actions.save') }}
                </BBtn>
            </div>
        </div>
    </form>
</template>
